import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { label: "持ち帰れるもの", href: "/#collections-grid" },
  { label: "放課後の時間割", href: "/#process" },
  { label: "よくある質問", href: "/#voices" },
  { label: "体験入部", href: "/#booking", highlight: true },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const forceBar = location.pathname !== "/";
  const showBar = scrolled || forceBar;

  const handleAnchor = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (location.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showBar ? "lum-nav-blur border-b border-[var(--lum-gold)]/15" : "bg-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 h-20">
        <Link
          to="/"
          className="group cursor-pointer"
          aria-label="社長の放課後部"
        >
          <h1 className="font-jp text-lg md:text-xl font-medium tracking-[0.08em] text-[var(--lum-cream)] relative inline-block">
            <span className="lum-wordmark">
              {"社長の放課後部".split("").map((ch, i) => (
                <span key={i}>{ch}</span>
              ))}
            </span>
            <span className="absolute -bottom-1 left-0 right-0 h-px bg-[var(--lum-gold)] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500" />
          </h1>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          {navItems.map((item) => (
            item.highlight ? (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleAnchor(e, item.href)}
                className="group inline-flex items-center gap-2 px-5 py-2 bg-[var(--lum-gold)] text-white font-jp text-[13px] font-medium rounded-full hover:bg-[#1d4ed8] transition-all duration-300 cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
              >
                {item.label}
                <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleAnchor(e, item.href)}
                className="group relative font-jp text-[13px] text-[var(--lum-cream)]/80 hover:text-[var(--lum-gold)] transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="relative">{item.label}</span>
                <span className="absolute left-0 right-0 -bottom-2 h-px bg-[var(--lum-gold)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </a>
            )
          ))}
        </div>

        <button
          type="button"
          aria-label="Menu"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-[var(--lum-cream)] cursor-pointer"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden lum-nav-blur border-t border-[var(--lum-gold)]/15">
          <div className="flex flex-col px-6 py-6 gap-5">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  handleAnchor(e, item.href);
                  setMobileOpen(false);
                }}
                className={`font-jp text-[14px] cursor-pointer ${
                  item.highlight ? "text-[var(--lum-gold)] font-medium" : "text-[var(--lum-cream)]/80"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}